import { PDFDocument, PDFPage, StandardFonts, rgb } from 'pdf-lib';
import {
  renderAcceptanceLetter,
  resolveAcceptanceLetterTemplateVariant,
  resolveChildGenderWords,
} from './acceptanceLetterTemplates';

const LETTER_PAGE_WIDTH = 612;
const LETTER_PAGE_HEIGHT = 792;
const LEFT_MARGIN = 56;
const TOP_MARGIN = 56;
const BOTTOM_MARGIN = 56;
const BODY_FONT_SIZE = 12;
const SMALL_FONT_SIZE = 9;
const TITLE_FONT_SIZE = 18;
const LINE_HEIGHT = 18;
const MAX_BODY_CHARS_PER_LINE = 86;
const PDF_BLOB_REVOKE_DELAY_MS = 60_000;
const PDF_FALLBACK_SHELL_BLOB_REVOKE_DELAY_MS = 10 * 60_000;
const LOGO_BOX_SIZE = 72;
const HEADER_GAP = 18;
const BODY_TEXT_COLOR = rgb(0.13, 0.17, 0.2);
const MUTED_TEXT_COLOR = rgb(0.39, 0.45, 0.5);
const BRAND_COLOR = rgb(0.07, 0.38, 0.36);
const BRAND_SOFT_COLOR = rgb(0.91, 0.97, 0.96);
const BORDER_COLOR = rgb(0.82, 0.88, 0.88);

export type AdmissionsAcceptanceLetterStudent = {
  id?: string;
  fullName: string;
  firstName?: string;
  gradeApplyingFor?: string;
  division?: string;
  gender?: string;
  status?: string;
  enrollmentDeadlineLabel?: string;
};

export type AdmissionsAcceptanceLetterInput = {
  applicationId: string;
  applicationCode: string;
  familyName: string;
  parentSalutation: string;
  academicYear: string;
  acceptedDateLabel?: string;
  schoolName?: string;
  schoolAddress?: string;
  schoolPhone?: string;
  schoolEmail?: string;
  schoolLogoDataUrl?: string | null;
  admissionsOfficeLabel?: string;
  schoolAdmissionsSignerName?: string;
  schoolAdmissionsSignerTitle?: string;
  schoolDivisionName?: string;
  portalUrl?: string;
  missionStatement?: string;
  registrationFeeDescription?: string;
  students: readonly AdmissionsAcceptanceLetterStudent[];
};

export type AdmissionsAcceptanceLetterPdf = {
  bytes: Uint8Array;
  fileName: string;
};

export type OpenAdmissionsAcceptanceLetterPdfInput = {
  letter: AdmissionsAcceptanceLetterInput;
  targetWindow?: Window | null;
};

const defaultSchoolName = 'School Name';
const defaultSchoolAddress = 'School address';
const defaultSchoolPhone = 'School phone';
const defaultSchoolEmail = 'Admissions email';
const defaultSchoolLogoLabel = 'School logo';
const defaultSignerName = '[Admissions signer name]';
const defaultSignerTitle = '[Admissions signer title]';
const defaultDivisionName = '[Division or school name]';

const readText = (value: unknown, fallback = ''): string => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text || fallback;
};

const sanitizePdfFileToken = (value: string): string =>
  readText(value, 'admissions')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'admissions';

export const buildAdmissionsAcceptanceLetterFileName = (
  input: Pick<AdmissionsAcceptanceLetterInput, 'applicationCode' | 'applicationId'>,
): string => `${sanitizePdfFileToken(readText(input.applicationCode, input.applicationId))}-acceptance-letter.pdf`;

const wrapLine = (line: string, limit = MAX_BODY_CHARS_PER_LINE): string[] => {
  const trimmed = line.trimEnd();
  if (!trimmed) {
    return [''];
  }
  const words = trimmed.split(/\s+/).filter(Boolean);
  const rows: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= limit) {
      current = next;
      continue;
    }
    if (current) {
      rows.push(current);
    }
    current = word;
  }
  if (current) {
    rows.push(current);
  }
  return rows.length > 0 ? rows : [''];
};

const drawWrappedText = (
  page: PDFPage,
  text: string,
  x: number,
  y: number,
  options: {
    font: Awaited<ReturnType<PDFDocument['embedFont']>>;
    size?: number;
    lineHeight?: number;
    maxChars?: number;
    color?: ReturnType<typeof rgb>;
  },
): number => {
  const rows = wrapLine(text, options.maxChars ?? MAX_BODY_CHARS_PER_LINE);
  let nextY = y;
  for (const row of rows) {
    page.drawText(row, {
      x,
      y: nextY,
      size: options.size ?? BODY_FONT_SIZE,
      font: options.font,
      color: options.color ?? rgb(0.13, 0.17, 0.2),
    });
    nextY -= options.lineHeight ?? LINE_HEIGHT;
  }
  return nextY;
};

const decodeBase64 = (base64: string): Uint8Array | null => {
  if (typeof globalThis.atob !== 'function') {
    return null;
  }
  try {
    const binary = globalThis.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  } catch {
    return null;
  }
};

const embedLogoImage = async (pdf: PDFDocument, logoDataUrl: string) => {
  const match = logoDataUrl.match(/^data:image\/(png|jpe?g);base64,(.+)$/i);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  const bytes = decodeBase64(match[2]);
  if (!bytes) {
    return null;
  }
  try {
    return match[1].toLowerCase().startsWith('png')
      ? await pdf.embedPng(bytes)
      : await pdf.embedJpg(bytes);
  } catch {
    return null;
  }
};

const drawLogoFrame = async ({
  pdf,
  page,
  logoDataUrl,
  bodyFont,
}: {
  pdf: PDFDocument;
  page: PDFPage;
  logoDataUrl: string;
  bodyFont: Awaited<ReturnType<PDFDocument['embedFont']>>;
}) => {
  const logoX = LEFT_MARGIN;
  const logoY = LETTER_PAGE_HEIGHT - TOP_MARGIN - LOGO_BOX_SIZE + 2;
  const logoImage = await embedLogoImage(pdf, logoDataUrl);
  page.drawRectangle({
    x: logoX,
    y: logoY,
    width: LOGO_BOX_SIZE,
    height: LOGO_BOX_SIZE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    color: logoImage ? undefined : rgb(0.97, 0.98, 0.98),
  });
  if (logoImage) {
    const scale = Math.min((LOGO_BOX_SIZE - 10) / logoImage.width, (LOGO_BOX_SIZE - 10) / logoImage.height, 1);
    const dimensions = logoImage.scale(scale);
    page.drawImage(logoImage, {
      x: logoX + (LOGO_BOX_SIZE - dimensions.width) / 2,
      y: logoY + (LOGO_BOX_SIZE - dimensions.height) / 2,
      width: dimensions.width,
      height: dimensions.height,
    });
    return;
  }
  page.drawText(defaultSchoolLogoLabel.toUpperCase(), {
    x: logoX + 13,
    y: logoY + 38,
    size: 7.5,
    font: bodyFont,
    color: MUTED_TEXT_COLOR,
  });
  page.drawText('placeholder', {
    x: logoX + 17,
    y: logoY + 26,
    size: 7.5,
    font: bodyFont,
    color: MUTED_TEXT_COLOR,
  });
};

const drawMetadataCell = (
  page: PDFPage,
  label: string,
  value: string,
  x: number,
  y: number,
  width: number,
  fonts: {
    bodyFont: Awaited<ReturnType<PDFDocument['embedFont']>>;
    headingFont: Awaited<ReturnType<PDFDocument['embedFont']>>;
  },
) => {
  page.drawText(label.toUpperCase(), {
    x,
    y,
    size: 7.5,
    font: fonts.headingFont,
    color: MUTED_TEXT_COLOR,
  });
  drawWrappedText(page, value, x, y - 13, {
    font: fonts.bodyFont,
    size: 9.5,
    lineHeight: 11,
    maxChars: Math.max(16, Math.floor(width / 5.5)),
    color: BODY_TEXT_COLOR,
  });
};

const resolveStudentFirstName = (student: AdmissionsAcceptanceLetterStudent): string => {
  const firstName = readText(student.firstName);
  if (firstName) {
    return firstName;
  }
  return readText(student.fullName).split(/\s+/)[0] ?? 'your child';
};

const normalizeLetterInput = (input: AdmissionsAcceptanceLetterInput): Required<AdmissionsAcceptanceLetterInput> => {
  const students = input.students
    .map((student, index) => ({
      ...student,
      fullName: readText(student.fullName, `Student ${index + 1}`),
      firstName: readText(student.firstName, readText(student.fullName, `Student ${index + 1}`).split(/\s+/)[0]),
      gradeApplyingFor: readText(student.gradeApplyingFor, 'Not specified'),
      division: readText(student.division, 'School division not specified'),
      gender: readText(student.gender),
      status: readText(student.status, 'accepted'),
      enrollmentDeadlineLabel: readText(student.enrollmentDeadlineLabel),
    }))
    .filter((student) => student.fullName);
  return {
    applicationId: readText(input.applicationId, 'admissions-application'),
    applicationCode: readText(input.applicationCode, input.applicationId),
    familyName: readText(input.familyName, 'Family'),
    parentSalutation: readText(input.parentSalutation, `the ${readText(input.familyName, 'family')} family`),
    academicYear: readText(input.academicYear, 'the upcoming school year'),
    acceptedDateLabel: readText(input.acceptedDateLabel, new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })),
    schoolName: readText(input.schoolName, defaultSchoolName),
    schoolAddress: readText(input.schoolAddress, defaultSchoolAddress),
    schoolPhone: readText(input.schoolPhone, defaultSchoolPhone),
    schoolEmail: readText(input.schoolEmail, defaultSchoolEmail),
    schoolLogoDataUrl: readText(input.schoolLogoDataUrl),
    admissionsOfficeLabel: readText(input.admissionsOfficeLabel, 'Admissions Office'),
    schoolAdmissionsSignerName: readText(input.schoolAdmissionsSignerName, defaultSignerName),
    schoolAdmissionsSignerTitle: readText(input.schoolAdmissionsSignerTitle, defaultSignerTitle),
    schoolDivisionName: readText(input.schoolDivisionName, defaultDivisionName),
    portalUrl: readText(input.portalUrl),
    missionStatement: readText(
      input.missionStatement,
      'School mission statement and educational philosophy appear here.',
    ),
    registrationFeeDescription: readText(input.registrationFeeDescription, 'the required registration fee'),
    students: students.length > 0
      ? students
      : [{
          fullName: 'Student',
          firstName: 'Student',
          gradeApplyingFor: 'Not specified',
          division: 'School division not specified',
          gender: '',
          status: 'accepted',
          enrollmentDeadlineLabel: '',
        }],
  };
};

const toTemplateParagraphs = (value: string): string[] =>
  String(value ?? '')
    .replace(/\r\n/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const drawSignatureParagraph = (
  page: PDFPage,
  paragraph: string,
  x: number,
  y: number,
  options: {
    bodyFont: Awaited<ReturnType<PDFDocument['embedFont']>>;
    headingFont: Awaited<ReturnType<PDFDocument['embedFont']>>;
  },
): number => {
  const lines = paragraph
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);
  let nextY = drawWrappedText(page, lines[0] ?? 'Sincerely,', x, y, {
    font: options.bodyFont,
    color: BODY_TEXT_COLOR,
  });
  nextY -= 28;
  page.drawLine({
    start: { x, y: nextY + 10 },
    end: { x: x + 210, y: nextY + 10 },
    thickness: 0.8,
    color: BORDER_COLOR,
  });
  page.drawText('Authorized admissions signature', {
    x,
    y: nextY - 2,
    size: 7.5,
    font: options.bodyFont,
    color: MUTED_TEXT_COLOR,
  });
  nextY -= 20;
  for (const line of lines.slice(1).filter((entry) => entry.trim())) {
    nextY = drawWrappedText(page, line, x, nextY, {
      font: line === lines[1] ? options.headingFont : options.bodyFont,
      color: BODY_TEXT_COLOR,
    });
    nextY -= 2;
  }
  return nextY;
};

const renderStudentLetterParagraphs = (
  input: Required<AdmissionsAcceptanceLetterInput>,
  student: AdmissionsAcceptanceLetterStudent,
): string[] => {
  const genderWords = resolveChildGenderWords(student.gender || student.division);
  const variant = resolveAcceptanceLetterTemplateVariant({
    childStatus: student.status,
    division: student.division,
    gender: student.gender,
  });
  const rendered = renderAcceptanceLetter(variant, {
    currentDate: input.acceptedDateLabel,
    familyName: input.familyName,
    childName: student.fullName,
    childFirstName: resolveStudentFirstName(student),
    registrationFeeDescription: input.registrationFeeDescription,
    schoolYear: input.academicYear,
    schoolName: input.schoolName,
    schoolMission: input.missionStatement,
    schoolAddress: input.schoolAddress,
    schoolPhone: input.schoolPhone,
    schoolEmail: input.schoolEmail,
    schoolAdmissionsSignerName: input.schoolAdmissionsSignerName,
    schoolAdmissionsSignerTitle: input.schoolAdmissionsSignerTitle,
    schoolDivisionName: input.schoolDivisionName,
    ...genderWords,
  });
  return toTemplateParagraphs(rendered.content).slice(1);
};

const buildEnrollmentDeadlineText = (
  student: AdmissionsAcceptanceLetterStudent,
  firstName: string,
): string => {
  const enrollmentDeadline = readText(student.enrollmentDeadlineLabel);
  return enrollmentDeadline
    ? `Please complete the enrollment steps in the parent portal by ${enrollmentDeadline} so we can reserve ${firstName}'s seat.`
    : `Please complete the enrollment steps in the parent portal so we can reserve ${firstName}'s seat.`;
};

export const buildAdmissionsAcceptanceLetterTextPreview = (
  input: AdmissionsAcceptanceLetterInput,
): string[] => {
  const normalized = normalizeLetterInput(input);
  return normalized.students.map((student) => {
    const firstName = resolveStudentFirstName(student);
    const paragraphs = renderStudentLetterParagraphs(normalized, student)
      .filter((paragraph) => !/^dear\b/i.test(paragraph));
    const lines = [
      normalized.schoolName.toUpperCase(),
      defaultSchoolLogoLabel,
      normalized.admissionsOfficeLabel,
      'Acceptance Letter',
      `Date: ${normalized.acceptedDateLabel}`,
      `Application: ${normalized.applicationCode}`,
      `Academic year: ${normalized.academicYear}`,
      'Family Notification',
      `Dear ${normalized.parentSalutation},`,
      ...paragraphs,
      buildEnrollmentDeadlineText(student, firstName),
    ];
    if (normalized.portalUrl) {
      lines.push(`Parent portal: ${normalized.portalUrl}`);
    }
    lines.push(`${normalized.schoolAddress} | ${normalized.schoolPhone} | ${normalized.schoolEmail}`);
    return lines.join('\n');
  });
};

const drawAcceptanceLetterPage = async ({
  pdf,
  input,
  student,
}: {
  pdf: PDFDocument;
  input: Required<AdmissionsAcceptanceLetterInput>;
  student: AdmissionsAcceptanceLetterStudent;
}) => {
  const page = pdf.addPage([LETTER_PAGE_WIDTH, LETTER_PAGE_HEIGHT]);
  const headingFont = await pdf.embedFont(StandardFonts.HelveticaBold);
  const bodyFont = await pdf.embedFont(StandardFonts.Helvetica);
  const firstName = resolveStudentFirstName(student);

  await drawLogoFrame({
    pdf,
    page,
    logoDataUrl: input.schoolLogoDataUrl ?? '',
    bodyFont,
  });

  const headerTextX = LEFT_MARGIN + LOGO_BOX_SIZE + HEADER_GAP;
  const headerTopY = LETTER_PAGE_HEIGHT - TOP_MARGIN - 2;
  drawWrappedText(page, input.schoolName.toUpperCase(), headerTextX, headerTopY, {
    font: headingFont,
    size: TITLE_FONT_SIZE,
    lineHeight: 20,
    maxChars: 40,
    color: BRAND_COLOR,
  });
  page.drawText(input.admissionsOfficeLabel, {
    x: headerTextX,
    y: headerTopY - 36,
    size: BODY_FONT_SIZE,
    font: bodyFont,
    color: MUTED_TEXT_COLOR,
  });
  page.drawText('Acceptance Letter', {
    x: headerTextX,
    y: headerTopY - 54,
    size: 9,
    font: headingFont,
    color: BRAND_COLOR,
  });

  const headerRuleY = LETTER_PAGE_HEIGHT - TOP_MARGIN - LOGO_BOX_SIZE - 16;
  page.drawLine({
    start: { x: LEFT_MARGIN, y: headerRuleY },
    end: { x: LETTER_PAGE_WIDTH - LEFT_MARGIN, y: headerRuleY },
    thickness: 1.2,
    color: BRAND_COLOR,
  });

  const metadataY = headerRuleY - 18;
  page.drawRectangle({
    x: LEFT_MARGIN,
    y: metadataY - 38,
    width: LETTER_PAGE_WIDTH - (LEFT_MARGIN * 2),
    height: 48,
    borderColor: BORDER_COLOR,
    borderWidth: 0.7,
    color: BRAND_SOFT_COLOR,
  });
  const metadataWidth = (LETTER_PAGE_WIDTH - (LEFT_MARGIN * 2) - 32) / 3;
  drawMetadataCell(page, 'Date', input.acceptedDateLabel, LEFT_MARGIN + 14, metadataY - 8, metadataWidth, {
    bodyFont,
    headingFont,
  });
  drawMetadataCell(page, 'Application', input.applicationCode, LEFT_MARGIN + 14 + metadataWidth + 16, metadataY - 8, metadataWidth, {
    bodyFont,
    headingFont,
  });
  drawMetadataCell(page, 'Academic year', input.academicYear, LEFT_MARGIN + 14 + ((metadataWidth + 16) * 2), metadataY - 8, metadataWidth, {
    bodyFont,
    headingFont,
  });

  let y = metadataY - 68;
  page.drawText('Family Notification', {
    x: LEFT_MARGIN,
    y,
    size: 10,
    font: headingFont,
    color: BRAND_COLOR,
  });
  y -= 28;
  y = drawWrappedText(page, `Dear ${input.parentSalutation},`, LEFT_MARGIN, y, {
    font: bodyFont,
    color: BODY_TEXT_COLOR,
  });
  y -= 10;
  const paragraphs = renderStudentLetterParagraphs(input, student)
    .filter((paragraph) => !/^dear\b/i.test(paragraph));
  for (const paragraph of paragraphs) {
    if (/^sincerely[, ]*$/i.test(paragraph.split('\n')[0]?.trim() ?? '')) {
      y = drawSignatureParagraph(page, paragraph, LEFT_MARGIN, y, {
        bodyFont,
        headingFont,
      });
      y -= 4;
      continue;
    }
    y = drawWrappedText(page, paragraph, LEFT_MARGIN, y, {
      font: bodyFont,
      color: BODY_TEXT_COLOR,
    });
    y -= 10;
  }

  y = drawWrappedText(
    page,
    buildEnrollmentDeadlineText(student, firstName),
    LEFT_MARGIN,
    y,
    { font: bodyFont, color: BODY_TEXT_COLOR },
  );
  if (input.portalUrl) {
    y -= 6;
    y = drawWrappedText(page, `Parent portal: ${input.portalUrl}`, LEFT_MARGIN, y, {
      font: bodyFont,
      size: SMALL_FONT_SIZE,
      lineHeight: 14,
      color: BRAND_COLOR,
    });
  }

  page.drawLine({
    start: { x: LEFT_MARGIN, y: BOTTOM_MARGIN + 26 },
    end: { x: LETTER_PAGE_WIDTH - LEFT_MARGIN, y: BOTTOM_MARGIN + 26 },
    thickness: 1,
    color: BORDER_COLOR,
  });
  const footer = `${input.schoolAddress} | ${input.schoolPhone} | ${input.schoolEmail}`;
  drawWrappedText(page, footer, LEFT_MARGIN, BOTTOM_MARGIN + 8, {
    font: bodyFont,
    size: SMALL_FONT_SIZE,
    lineHeight: 12,
    maxChars: 110,
    color: MUTED_TEXT_COLOR,
  });
};

export const buildAdmissionsAcceptanceLetterPdf = async (
  input: AdmissionsAcceptanceLetterInput,
): Promise<AdmissionsAcceptanceLetterPdf> => {
  const normalized = normalizeLetterInput(input);
  const pdf = await PDFDocument.create();
  for (const student of normalized.students) {
    await drawAcceptanceLetterPage({ pdf, input: normalized, student });
  }
  return {
    bytes: await pdf.save(),
    fileName: buildAdmissionsAcceptanceLetterFileName(normalized),
  };
};

export const buildAdmissionsAcceptanceLetterPdfBytes = async (
  input: AdmissionsAcceptanceLetterInput,
): Promise<Uint8Array> => (await buildAdmissionsAcceptanceLetterPdf(input)).bytes;

const installPdfFallbackLink = (targetWindow: Window, url: string, fileName: string): HTMLAnchorElement | null => {
  const body = targetWindow.document.body;
  if (!body) {
    return null;
  }
  body.replaceChildren();
  const shell = targetWindow.document.createElement('main');
  shell.style.cssText = [
    'font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    'margin: 48px auto',
    'max-width: 560px',
    'line-height: 1.5',
    'color: #243b3a',
  ].join(';');
  const heading = targetWindow.document.createElement('h1');
  heading.textContent = 'Acceptance letter ready';
  heading.style.cssText = 'font-size: 24px; margin: 0 0 12px;';
  const message = targetWindow.document.createElement('p');
  message.textContent = 'If the PDF does not open automatically, use the button below.';
  message.style.cssText = 'margin: 0 0 24px;';
  const frame = targetWindow.document.createElement('iframe');
  frame.src = url;
  frame.dataset.testid = 'admissions-acceptance-letter-pdf-frame';
  frame.title = fileName;
  frame.style.cssText = [
    'display: block',
    'width: 100%',
    'height: min(70vh, 720px)',
    'border: 1px solid #d5e0df',
    'border-radius: 8px',
    'margin: 0 0 20px',
  ].join(';');
  const link = targetWindow.document.createElement('a');
  link.href = url;
  link.target = '_self';
  link.dataset.testid = 'admissions-acceptance-letter-fallback-link';
  link.textContent = 'Open PDF';
  link.style.cssText = [
    'display: inline-block',
    'border-radius: 8px',
    'background: #11645f',
    'color: white',
    'font-weight: 700',
    'padding: 12px 18px',
    'text-decoration: none',
  ].join(';');
  shell.append(heading, message, frame, link);
  body.append(shell);
  return link;
};

const openPdfBytes = (bytes: Uint8Array, fileName: string, targetWindow?: Window | null): boolean => {
  const normalizedBytes = new Uint8Array(bytes.length);
  normalizedBytes.set(bytes);
  const blob = new Blob([normalizedBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  if (targetWindow) {
    let fallbackLink: HTMLAnchorElement | null = null;
    try {
      targetWindow.document.title = fileName;
      fallbackLink = installPdfFallbackLink(targetWindow, url, fileName);
    } catch {
      // Browser PDF viewers can restrict access to the placeholder document.
    }
    try {
      if (fallbackLink) {
        fallbackLink.click();
      } else {
        targetWindow.location.href = url;
      }
    } catch {
      URL.revokeObjectURL(url);
      return false;
    }
    window.setTimeout(() => URL.revokeObjectURL(url), PDF_FALLBACK_SHELL_BLOB_REVOKE_DELAY_MS);
    return true;
  }
  const opened = window.open(url, '_blank');
  if (!opened) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), PDF_BLOB_REVOKE_DELAY_MS);
    return true;
  }
  try {
    opened.opener = null;
    opened.document.title = fileName;
  } catch {
    // Cross-browser PDF viewers may not expose the generated blob document.
  }
  window.setTimeout(() => URL.revokeObjectURL(url), PDF_BLOB_REVOKE_DELAY_MS);
  return true;
};

export const openAdmissionsAcceptanceLetterPdf = async ({
  letter,
  targetWindow,
}: OpenAdmissionsAcceptanceLetterPdfInput): Promise<AdmissionsAcceptanceLetterPdf> => {
  const pdf = await buildAdmissionsAcceptanceLetterPdf(letter);
  const opened = openPdfBytes(pdf.bytes, pdf.fileName, targetWindow);
  if (!opened) {
    throw new Error('Unable to open generated admissions acceptance-letter PDF.');
  }
  return pdf;
};
